<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :padding="false"
        size="md">
        <template v-slot:header>
            <div class="p-6">
                Media
            </div>
        </template>

        <div class="p-12 pt-10 flex flex-col gap-4">
            <vx-alert :closeable="false">
                <span v-if="props.blockImages && !props.blockFiles"><strong>Corporate</strong> only allows you to send files in the following formats: mp4, mp3, csv, pdf, vcard</span>
                <span v-else-if="!props.blockImages && props.blockFiles"><strong>Corporate</strong> only allows you to send files in the following formats: gif, jpg, jpeg, png, heic</span>
                <span v-else>Allowed media types:<br/> gif, jpg, jpeg, png, heic, csv, pdf, vcard, mp4, mp3</span>
            </vx-alert>
            <input type="file"
                id="media-input"
                class="absolute opacity-0"
                @change="fileChange">
            <label v-if="!url"
                data-test="media-drop"
                for="media-input"
                class="px-8 py-16 bg-slate-50 rounded-2xl border-dashed border-2 border-slate-300 flex-col justify-center items-center gap-2 flex cursor-pointer mb-0"
                :class="{
                    'border-sky-200 bg-white': dragging,
                }"
                @dragenter.prevent.stop="dragging = true"
                @dragover.prevent.stop="dragging = true"
                @dragleave.prevent.stop="dragging = false"
                @drop.prevent.stop="dropped">
                <font-awesome-icon widthAuto v-if="!uploading"
                    :icon="faPhotoFilmMusic"
                    class="text-sky-400 text-5xl">
                </font-awesome-icon>
                <vx-icon-loading v-else
                    class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                <div class="text-center text-balance text-slate-700 text-sm font-normal">
                    <template v-if="uploading">
                        Uploading...
                    </template>
                    <template v-else>
                        <span class="touch:hidden">Drag or paste media here, or<br/> click to open file browser.</span>
                        <span class="hidden touch:inline">Drag media here, or tap to choose a file.</span>
                    </template>
                </div>
            </label>
            <div v-if="url"
                class="w-96 max-w-full mx-auto">
                <message-builder-media-preview :mediaUrl="url"
                    :mediaContentType="contentType"
                    @remove="remove()"
                    removeable>
                </message-builder-media-preview>
            </div>
            <div class="text-center text-slate-500 text-sm">
                Limited to one attachment with 1000KiB file size.
                <template v-if="url">
                    <br />
                    Remove existing file to add a new one.
                </template>
            </div>
            <vx-alert v-if="error"
                data-test="media-error"
                :closeable="false"
                color="danger"
                class="whitespace-pre-line">{{ error }}</vx-alert>
            <vx-alert v-if="warning"
                color="info"
                :closeable="false">
                {{ warning }}
            </vx-alert>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click.prevent="emit('update:visible', false)"
                    color="muted"
                    size="lg"
                    type="button"
                    data-test="cancel-btn">
                    Cancel
                </vx-button>
                <vx-button @click.prevent="save()"
                    type="button"
                    color="primary"
                    size="lg"
                    class="grow"
                    data-test="save-btn"
                    :disabled="!url"
                    :loading="uploading">
                    Save
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxButton, VxModal, VxAlert, VxIconLoading } from '@voxie/frontend-components';
import { nextTick, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { messageMediaClient } from '~/services';
import { faCircleCheck, faPhotoFilmMusic } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MessageBuilderMediaPreview from './MessageBuilderMediaPreview.vue';
import { uploadMedia } from './mediaUpload';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    visible: Boolean,
    mediaUrl: {
        type: String,
        required: false,
    },
    mediaContentType: {
        type: String,
        required: false,
    },
    blockImages: {
        type: Boolean,
        default: false,
    },
    blockFiles: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:visible', 'update:mediaUrl', 'update:mediaContentType']);

const dragging = ref(false);
const uploading = ref(false);

const error = ref('');
const warning = ref('');
const url = ref(undefined);
const contentType = ref(undefined);
const uploadCancelKey = Symbol('media-upload');
let modalSession = 0;

const dropped = (event) => {
    if (event.dataTransfer.files[0]) {
        upload(event.dataTransfer.files[0]);
    }
    dragging.value = false;
}

const isTextField = (target) =>
    target instanceof HTMLElement && (target.isContentEditable || null !== target.closest('textarea, input:not([type="file"])'));

const pasted = (event) => {
    const file = event.clipboardData?.files[0];

    if (event.defaultPrevented || uploading.value || url.value || !file || isTextField(event.composedPath()[0])) {
        return;
    }

    event.preventDefault();
    upload(file);
}

useEventListener(() => (props.visible ? window : null), 'paste', pasted);

const fileChange = (event) => {
    if (event.target.files[0]) {
        upload(event.target.files[0]);
        event.target.value = '';
    }
}

const upload = async (file) => {
    if (uploading.value) {
        return;
    }

    const session = modalSession;
    const owner = { cancelKey: uploadCancelKey, isCurrent: () => session === modalSession };

    try {
        uploading.value = true;
        remove();
        const media = await uploadMedia(file, props, owner);

        if (session !== modalSession) {
            return;
        }

        url.value = media.url;
        contentType.value = media.contentType;
        warning.value = media.warning;
    } catch (e) {
        if (session === modalSession) {
            error.value = catchRequestError(e);
        }
    } finally {
        if (session === modalSession) {
            uploading.value = false;
        }
    }
}

const remove = () => {
    error.value = '';
    warning.value = '';
    url.value = undefined;
    contentType.value = undefined;
}

const save = async () => {
    if (uploading.value) {
        return;
    }

    emit('update:mediaContentType', contentType.value);
    await nextTick();
    emit('update:mediaUrl', url.value);
    emit('update:visible', false)
}

watch(() => props.visible, () => {
    modalSession++;
    messageMediaClient.cancelRequest(uploadCancelKey);
    uploading.value = false;
    dragging.value = false;
    error.value = '';
    warning.value = '';
    url.value = props.mediaUrl;
    contentType.value = props.mediaContentType;
});

defineExpose({
    upload,
});
</script>
